import React, { FC, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import YupDate from "yup/lib/date";
import YupObject from "yup/lib/object";
import YupRef from "yup/lib/Reference";
import YupLazy from "yup/lib/Lazy";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { DatePicker } from "components/DatePicker/DatePicker";
import ButtonGroup, { OptionType } from "./components/ButtonGroup";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import StyledPatientRecovery from "./PatientRecovery.style";
import AddNewValue from "./components/AddNewValuePopover";
import AdditionalControls from "./components/AdditionalControls";
import SuccessFailedModal from "components/SuccessFailedModal/SuccessFailedModal";
import ExamCadence from "./components/ExamCadence";
import { ReusableModal } from "components/ReusableModal/ReusableModal";
import {
  DEFAULT_END_DATE_DURATION,
  DURATION_OPTIONS,
  EXTENSION_OPTIONS,
  GOALS_OPTIONS,
  SUGGESTED_EXAM_CADENCE_OPTIONS,
  TextFieldMaskENUM,
  TYPE_OPTIONS,
  TYPE_OPTIONS_ENABLED,
  WEIGHT_BEARING_OPTIONS,
} from "../../../../../constants/patientRegion";
import { RecoveryPlanType } from "types/RecoveryPlan";
import { PatientRepository } from "../../../../../repositories/PatientRepository";
import { JointType } from "types/Patient";
import { getTimeDiffInMS } from "../../../../../utils/general";
import Button from "@mui/material/Button";
import { RecoveryPlanItem } from "../../../../../constants/clinic";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/reduxHooks";
import {
  selectClinicRecoveryTemplates,
  selectCurrentClinician,
} from "../../../../../store/ClinicianStore/selectors";
import { ClinicRepository } from "../../../../../repositories/ClinicRepository";
import { getClinicRecoveryTemplates } from "../../../../../store/ClinicianStore/actions";
import { useParams } from "react-router-dom";

const validationSchema = new YupObject().shape({
  startDate: new YupDate().nullable(),
  endDate: new YupLazy((value) => {
    if (value === null) {
      return new YupDate().nullable();
    }
    return new YupDate().min(
      new YupRef("startDate"),
      "End date must be after or equal to start date"
    );
  }),
});

export const PatientRecovery: FC<{
  recoveryPlan?: RecoveryPlanType | null;
  patientId?: string;
  setJointData?: any;
  jointData?: any;
  updateRecoveryPlanData?: (recoveryPlan?: RecoveryPlanType) => void;
  isTemplate?: boolean;
}> = ({
  recoveryPlan,
  patientId,
  setJointData,
  jointData,
  updateRecoveryPlanData,
  isTemplate,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const [examCadenceOptions, setExamCadenceOptions] = useState(
    SUGGESTED_EXAM_CADENCE_OPTIONS
  );
  const [defaultExamOptions, setDefaultExamOptions] = useState(
    SUGGESTED_EXAM_CADENCE_OPTIONS
  );
  const currentRecoveryTemplates: string[] | undefined = useAppSelector(
    selectClinicRecoveryTemplates
  );
  const recoveryTemplates =
    currentRecoveryTemplates?.map((item: any) => ({
      label: item.name,
      value: item._id,
    })) || [];
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const params: { id: string } = useParams();
  const currentPage = location.pathname.split("/")[1];
  const isEditPage = currentPage === "edit-template";
  const templateToCompare = isEditPage ? params.id : selectedTemplate;
  const templateToSelect: any = currentRecoveryTemplates?.find(
    (element: any) => element._id === templateToCompare
  );
  const [contralateralExamCadenceOptions, setContralateralExamCadenceOptions] =
    useState(SUGGESTED_EXAM_CADENCE_OPTIONS);
  const [goalsOptions, setGoalsOptions] = useState(GOALS_OPTIONS);
  const [defaultContralateralExamOptions, setDefaultContralateralExamOptions] =
    useState(SUGGESTED_EXAM_CADENCE_OPTIONS);
  const [defaultActiveExtensionOptions, setDefaultActiveExtensionOptions] =
    useState(EXTENSION_OPTIONS);
  const [defaultPassiveExtensionOptions, setDefaultPassiveExtensionOptions] =
    useState(EXTENSION_OPTIONS);
  const [defaultActiveFlexionOptions, setDefaultActiveFlexionOptions] =
    useState(EXTENSION_OPTIONS);
  const [defaultPassiveFlexionOptions, setDefaultPassiveFlexionOptions] =
    useState(EXTENSION_OPTIONS);
  const [iconElement, setIconElement] = useState<Element | null>(null);
  const [editableField, setEditableField] = useState<RecoveryPlanItem | "">("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasUnsavedEndDate, setHasUnsavedEndDate] = useState(false);
  const [inputPattern, setInputPattern] = useState(
    TextFieldMaskENUM.examCadence
  );
  const [selectedGoalPeriod, setSelectedGoalPeriod] =
    useState<OptionType | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>("");
  const currentClinician = useAppSelector(selectCurrentClinician);
  const mappedComments = goalsOptions.reduceRight((acc, currentValue) => {
    // @ts-ignore
    acc[currentValue.label] = "";
    return acc;
  }, {});

  const [comment, setComment] = useState(mappedComments);

  const closePopover = () => {
    setEditableField("");
    setIconElement(null);
  };

  const setGoals = (
    goalsToSet:
      | {
          [key: string]: {
            activeExtension?: string;
            passiveExtension?: string;
            activeFlexion?: string;
            passiveFlexion?: string;
            weightBearing?: string;
            comment?: string;
          };
        }
      | undefined
  ) => {
    const goals = {} as {
      [key: string]: {
        activeExtension?: OptionType | null;
        passiveExtension?: OptionType | null;
        activeFlexion?: OptionType | null;
        passiveFlexion?: OptionType | null;
        weightBearing?: OptionType;
        comment?: string;
      };
    };

    const goalsKeys = Object.keys(goalsToSet || {});

    if (goalsKeys.length) {
      const selectedGoal = goalsOptions.find(
        (option) => option.value === goalsKeys[0]
      );

      if (selectedGoal) {
        setSelectedGoalPeriod(selectedGoal);
      }
    }

    Object.entries(goalsToSet || {}).forEach(([key, goal]) => {
      goals[key] = {
        activeExtension: mapGoalsOption(
          goal?.activeExtension,
          defaultActiveExtensionOptions,
          setDefaultActiveExtensionOptions
        ),
        passiveExtension: mapGoalsOption(
          goal?.passiveExtension,
          defaultPassiveExtensionOptions,
          setDefaultPassiveExtensionOptions
        ),
        activeFlexion: mapGoalsOption(
          goal?.activeFlexion,
          defaultActiveFlexionOptions,
          setDefaultActiveFlexionOptions
        ),
        passiveFlexion: mapGoalsOption(
          goal?.passiveFlexion,
          defaultPassiveFlexionOptions,
          setDefaultPassiveFlexionOptions
        ),
        weightBearing: WEIGHT_BEARING_OPTIONS.find(
          (option) => option.value === goal?.weightBearing
        ),
        comment: goal?.comment || "",
      };

      setGoalsOptions((prev) =>
        prev.some((option) => option.value === key)
          ? prev
          : [...prev, { value: key, label: key }]
      );
      if (goal?.comment) {
        setComment((prevComments) => ({
          ...prevComments,
          [key]: goal.comment,
        }));
      }
    });
    return goals;
  };

  function mapGoalsOption(
    goalOption: string | undefined,
    defaultOptions: OptionType[],
    setDefaultOptions: React.Dispatch<React.SetStateAction<OptionType[]>>
  ) {
    let mappedOption = defaultOptions.find(
      (option) => option.value === goalOption
    );

    if (!mappedOption && goalOption) {
      const newOption = {
        value: goalOption,
        label: goalOption,
        withDegree: true,
      };
      setDefaultOptions((prev) => [newOption, ...prev]);
      mappedOption = newOption;
    }

    return mappedOption;
  }

  const initialValues = useMemo(() => {
    const goals = setGoals(recoveryPlan?.goals);

    let examCadence;
    let contralateralExamCadence;

    if (recoveryPlan?.examCadence) {
      examCadence = SUGGESTED_EXAM_CADENCE_OPTIONS.find(
        (option) => option.value === recoveryPlan?.examCadence
      );
      if (!examCadence) {
        const option = {
          value: recoveryPlan.examCadence,
          label: recoveryPlan.examCadence,
        };

        setExamCadenceOptions((prev) => [...prev, option]);
        setDefaultExamOptions((prev) => [...prev, option]);

        examCadence = option;
      }
    }

    if (recoveryPlan?.contralateralExamCadence) {
      contralateralExamCadence = SUGGESTED_EXAM_CADENCE_OPTIONS.find(
        (option) => option.value === recoveryPlan?.contralateralExamCadence
      );
      if (!contralateralExamCadence) {
        const option = {
          value: recoveryPlan.contralateralExamCadence,
          label: recoveryPlan.contralateralExamCadence,
        };

        setContralateralExamCadenceOptions((prev) => [...prev, option]);
        setDefaultContralateralExamOptions((prev) => [...prev, option]);

        contralateralExamCadence = option;
      }
    }

    return {
      duration:
        DURATION_OPTIONS.find(
          (option) => option.value === recoveryPlan?.duration
        ) ?? null,
      examCadence,
      startDate: recoveryPlan?.startDate
        ? new Date(recoveryPlan.startDate)
        : null,
      endDate: recoveryPlan?.endDate ? new Date(recoveryPlan.endDate) : null,
      type: TYPE_OPTIONS.find((option) => option.value === recoveryPlan?.type),
      isContralateralNeed: Boolean(contralateralExamCadence),
      contralateralExamCadence,
      goals,
    };
  }, []);

  useEffect(() => {
    if (templateToSelect) {
      isEditPage && setTemplateName(templateToSelect.name);
      formik.setFieldValue(
        "duration",
        DURATION_OPTIONS.find(
          (option) => option.value === templateToSelect?.duration
        )
      );
      formik.setFieldValue(
        "examCadence",
        SUGGESTED_EXAM_CADENCE_OPTIONS.find(
          (option) => option.value === templateToSelect?.examCadence
        )
      );
      formik.setFieldValue("startDate", new Date(templateToSelect.startDate));
      formik.setFieldValue("endDate", new Date(templateToSelect.endDate));
      formik.setFieldValue("goals", setGoals(templateToSelect?.goals));

      if (templateToSelect?.contralateralExamCadence) {
        let contralateralExamCadence = SUGGESTED_EXAM_CADENCE_OPTIONS.find(
          (option) =>
            option.value === templateToSelect?.contralateralExamCadence
        );
        if (!contralateralExamCadence) {
          contralateralExamCadence = {
            value: templateToSelect.contralateralExamCadence,
            label: templateToSelect.contralateralExamCadence,
          };
        }
        formik.setFieldValue(
          "isContralateralNeed",
          Boolean(contralateralExamCadence)
        );
        formik.setFieldValue(
          "contralateralExamCadence",
          contralateralExamCadence
        );
      }
      formik.setFieldValue(
        "type",
        TYPE_OPTIONS.find((option) => option.value === templateToSelect?.type)
      );
    }
  }, [templateToSelect]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (data) => {
      try {
        setIsLoading(true);
        const goals: {
          [key: string]: {
            activeExtension?: string;
            passiveExtension?: string;
            activeFlexion?: string;
            passiveFlexion?: string;
            weightBearing?: string;
            comment?: string;
          };
        } = {};

        Object.entries(data.goals || {}).forEach(([key, goal]) => {
          if (
            goal?.activeExtension ||
            goal?.passiveExtension ||
            goal?.activeFlexion ||
            goal?.passiveFlexion ||
            goal?.weightBearing ||
            goal?.comment
          ) {
            goals[key] = {
              activeExtension: goal.activeExtension?.value,
              passiveExtension: goal.passiveExtension?.value,
              activeFlexion: goal.activeFlexion?.value,
              passiveFlexion: goal.passiveFlexion?.value,
              weightBearing: goal.weightBearing?.value,
              comment: goal.comment,
            };
          }
        });
        const body: {
          contralateralExamCadence?: string;
          duration?: string;
          examCadence?: string;
          startDate?: Date;
          endDate?: Date;
          type?: string;
          jointId?: string;
          patientId?: string;
          name?: string;
          clinicId?: string;
          comment?: string;
          goals: {
            [key: string]: {
              activeExtension?: string;
              passiveExtension?: string;
              activeFlexion?: string;
              passiveFlexion?: string;
              weightBearing?: string;
              comment?: string;
            };
          };
        } = {
          contralateralExamCadence: data.contralateralExamCadence?.value || "",
          duration: data.duration?.value || "",
          examCadence: data.examCadence?.value || "",
          startDate: data.startDate || ("" as unknown as Date),
          endDate: data.endDate || ("" as unknown as Date),
          type: data.type?.value || "",
          goals,
        };

        const patientRepository = PatientRepository.getInstance();
        const clinicRepository = ClinicRepository.getInstance();

        if (currentClinician && isTemplate) {
          body.name = templateName;
          body.clinicId = currentClinician.clinicId;
          isEditPage
            ? await clinicRepository.editRecoveryTemplate(
                body,
                templateToSelect._id
              )
            : await clinicRepository.createRecoveryTemplate(body);
          setIsSuccessModalOpen(true);
          if (!isEditPage) {
            formik.setValues(initialValues);
            setTemplateName("");
            setSelectedGoalPeriod(null);
            setComment(mappedComments);
          }
          return;
        }

        if (recoveryPlan?._id) {
          await patientRepository.updateRecoveryPlan(recoveryPlan._id, body);
        } else {
          {
            body.jointId = (recoveryPlan?.joint as JointType)?._id;
            body.patientId = patientId;
            const newRecoveryPlan = await patientRepository.createRecoveryPlan(
              body
            );
            updateRecoveryPlanData && updateRecoveryPlanData(newRecoveryPlan);
          }
        }

        if (hasUnsavedEndDate) {
          setHasUnsavedEndDate(false);
        }
        setIsLoading(false);
        setIsSuccessModalOpen(true);
      } catch (err) {
        setIsError(true);
        setIsLoading(false);
        setIsSuccessModalOpen(true);
      }
    },
  });

  const configuredGoals = useMemo(() => {
    const configured: string[] = [];
    Object.entries(formik.values.goals || {}).forEach(([key, goal]) => {
      if (
        goal?.activeExtension ||
        goal?.passiveExtension ||
        goal?.activeFlexion ||
        goal?.passiveFlexion ||
        goal?.weightBearing ||
        goal?.comment
      ) {
        configured.push(key);
      }
    });
    return configured;
  }, [formik.values.goals]);

  const handleEditableNotIncluded = (value: string) => {
    if (editableField === RecoveryPlanItem.examCadence) {
      const existingOption = examCadenceOptions.find(
        (option) => option.value === value
      );
      if (!existingOption) {
        const option = { value, label: value };

        setExamCadenceOptions((prev) => {
          return [...prev, option];
        });
      }
    }

    if (editableField === RecoveryPlanItem.examGoals) {
      const existingOption = goalsOptions.find(
        (option) => option.value === value
      );
      if (!existingOption) {
        const option = { value, label: value };

        setGoalsOptions((prev) => {
          return [...prev, option];
        });

        setComment((prev) => {
          return { ...prev, [option.label]: "" };
        });
      }
    }

    if (editableField === RecoveryPlanItem.contralateralExamCadence) {
      const existingOption = contralateralExamCadenceOptions.find(
        (option) => option.value === value
      );
      if (!existingOption) {
        const option = { value, label: value };

        setContralateralExamCadenceOptions((prev) => {
          return [...prev, option];
        });
      }
    }
  };

  const updateOptions = async (value: string) => {
    if (
      editableField &&
      [
        RecoveryPlanItem.activeExtension,
        RecoveryPlanItem.passiveExtension,
        RecoveryPlanItem.activeFlexion,
        RecoveryPlanItem.passiveFlexion,
      ].includes(editableField)
    ) {
      const [one, two] = value.split("-");
      let formattedValue = `${one} - ${two}`;
      if (!one) {
        formattedValue = "0 - 0";
      }
      if (!two) {
        formattedValue = `${one} - 0`;
      }
      const option = {
        value: formattedValue,
        label: formattedValue,
        withDegree: true,
      };
      let setFunc = setDefaultActiveExtensionOptions;
      switch (editableField) {
        case "passiveExtension":
          setFunc = setDefaultPassiveExtensionOptions;
          break;
        case "activeFlexion":
          setFunc = setDefaultActiveFlexionOptions;
          break;
        case "passiveFlexion":
          setFunc = setDefaultPassiveFlexionOptions;
          break;

        default:
          break;
      }

      setFunc((prev) => {
        return [{ ...option, withDegree: true }, prev[1]];
      });
    } else {
      handleEditableNotIncluded(value);
    }

    setIconElement(null);
    setEditableField("");
  };

  const resetExamCadenceOptions = () => {
    if (
      !defaultExamOptions.some(
        (option) => option.value === formik.values.examCadence?.value
      )
    ) {
      formik.setFieldValue("examCadence", null);
    }
    setExamCadenceOptions(defaultExamOptions);
  };

  const resetContralateralExamCadenceOptions = () => {
    if (
      !goalsOptions.some(
        (option) => option.value === formik.values.goals?.value
      )
    ) {
      formik.setFieldValue("contralateralExamCadence", null);
    }
    setContralateralExamCadenceOptions(defaultContralateralExamOptions);
  };

  useEffect(() => {
    if (!selectedGoalPeriod) {
      return;
    }
    const selectedGoal = formik.values.goals?.[selectedGoalPeriod?.value];
    const activeExtension = selectedGoal?.activeExtension?.value;
    const passiveExtension = selectedGoal?.passiveExtension?.value;
    const activeFlexion = selectedGoal?.activeFlexion?.value;
    const passiveFlexion = selectedGoal?.passiveFlexion?.value;

    if (activeExtension && activeExtension !== "Full") {
      setDefaultActiveExtensionOptions((prev) => [
        selectedGoal?.activeExtension as OptionType,
        prev[prev.length - 1],
      ]);
    }

    if (passiveExtension && passiveExtension !== "Full") {
      setDefaultPassiveExtensionOptions((prev) => [
        selectedGoal?.passiveExtension as OptionType,
        prev[prev.length - 1],
      ]);
    }

    if (activeFlexion && activeFlexion !== "Full") {
      setDefaultActiveFlexionOptions((prev) => [
        selectedGoal?.activeFlexion as OptionType,
        prev[prev.length - 1],
      ]);
    }

    if (passiveFlexion && passiveFlexion !== "Full") {
      setDefaultPassiveFlexionOptions((prev) => [
        selectedGoal?.passiveFlexion as OptionType,
        prev[prev.length - 1],
      ]);
    }
  }, [selectedGoalPeriod]);

  useEffect(() => {
    if (defaultActiveExtensionOptions.length && selectedGoalPeriod) {
      formik.setFieldValue(
        `goals[${selectedGoalPeriod?.value}].activeExtension`,
        defaultActiveExtensionOptions[0]
      );
    }
  }, [defaultActiveExtensionOptions]);

  useEffect(() => {
    if (defaultPassiveExtensionOptions.length && selectedGoalPeriod) {
      formik.setFieldValue(
        `goals[${selectedGoalPeriod?.value}].passiveExtension`,
        defaultPassiveExtensionOptions[0]
      );
    }
  }, [defaultPassiveExtensionOptions]);

  useEffect(() => {
    if (defaultActiveFlexionOptions.length && selectedGoalPeriod) {
      formik.setFieldValue(
        `goals[${selectedGoalPeriod?.value}].activeFlexion`,
        defaultActiveFlexionOptions[0]
      );
    }
  }, [defaultActiveFlexionOptions]);

  useEffect(() => {
    if (defaultPassiveFlexionOptions.length && selectedGoalPeriod) {
      formik.setFieldValue(
        `goals[${selectedGoalPeriod?.value}].passiveFlexion`,
        defaultPassiveFlexionOptions[0]
      );
    }
  }, [defaultPassiveFlexionOptions]);

  useEffect(() => {
    if (
      recoveryPlan?.startDate &&
      recoveryPlan?.duration &&
      !recoveryPlan?.endDate
    ) {
      setHasUnsavedEndDate(true);
      setEndDate(new Date(recoveryPlan?.startDate), recoveryPlan?.duration);
    }
    dispatch(getClinicRecoveryTemplates(currentClinician?.clinicId));
  }, []);
  const getTypeOptions = () => {
    return TYPE_OPTIONS.filter((type) =>
      TYPE_OPTIONS_ENABLED.includes(type.value)
    );
  };

  const setEndDate = (
    startDateProvided?: Date | null,
    providedDuration?: string | null
  ) => {
    const startDate =
      startDateProvided ?? formik.values.startDate ?? recoveryPlan?.startDate;
    const duration =
      providedDuration ??
      formik.values.duration?.value ??
      recoveryPlan?.duration ??
      DEFAULT_END_DATE_DURATION;
    if (!startDate) {
      return () => {};
    }
    const time = duration.slice(0, duration.length - 1);
    const timeUnit = duration.slice(-1);
    const recoveryPlanDurationInMS = getTimeDiffInMS(time, timeUnit);
    const endDate = startDate.getTime() + recoveryPlanDurationInMS;
    const date = new Date(endDate);

    formik.setFieldValue("endDate", date);
  };

  const unmountRecoveryPlan = () => {
    if (!recoveryPlan?.joint || jointData.leftJoint || !jointData.rightJoint) {
      return () => {};
    }

    const sideToUpdate = recoveryPlan.joint.side;
    const updatedJointData = {
      ...jointData,
      [`${sideToUpdate}Joint`]: {
        ...jointData[`${sideToUpdate}Joint`],
        recoveryPlan: null,
      },
    };

    setJointData(updatedJointData);
  };
  const handleDeleteRecoveryPlan = async () => {
    if (!recoveryPlan?._id) {
      return { error: "Missing recovery plan ID" };
    }
    try {
      const patientRepository = PatientRepository.getInstance();
      await patientRepository.deleteRecoveryPlan(recoveryPlan._id);

      setTimeout(() => {
        unmountRecoveryPlan();
      }, 2500);
    } catch (error) {
      return { error: "Something went wrong please try again!" };
    }
  };

  // @ts-ignore
  return (
    <StyledPatientRecovery className="container max-w-[1024px] m-auto">
      {!isTemplate && (
        <div className="absolute top-7 right-5 flex">
          <AdditionalControls
            onApplyClick={formik.submitForm}
            selectedTemplate={selectedTemplate}
            recoveryTemplates={recoveryTemplates}
            onRevertClick={() => formik.setValues(initialValues)}
            isLoading={formik.isSubmitting}
            onSelectTemplate={(e: any) => setSelectedTemplate(e.target.value)}
          />
          <IconButton
            className="right-[-5px]"
            onClick={() => {
              setOpenDeleteModal(true);
            }}
            disabled={!recoveryPlan?._id}
          >
            <DeleteIcon color="error" />
          </IconButton>
        </div>
      )}
      <div className="w-full">
        <div className="flex space-x-10">
          <div className="flex flex-1 flex-col space-y-[25px] max-w-[450px] flex-wrap">
            <div>
              {isTemplate && (
                <div>
                  <p className={"mb-3 text-xl font-bold"}>Recovery Plan</p>
                  <h5 className="text-[12px] text-[#231F20] mb-2">
                    Template Name
                  </h5>
                  <TextField
                    inputProps={{
                      style: {
                        height: "20px",
                      },
                    }}
                    size="small"
                    variant="outlined"
                    placeholder="Type name here..."
                    name="comments"
                    fullWidth
                    onChange={(e) => setTemplateName(e.target.value)}
                    value={templateName}
                  />
                </div>
              )}
              <h5 className="text-[12px] text-[#231F20] mt-3">Duration</h5>
              <div className="flex items-center space-x-1 mt-2.5 flex-wrap">
                <ButtonGroup
                  options={DURATION_OPTIONS}
                  selectedOption={formik.values.duration}
                  handleChange={(option) => {
                    formik.setFieldValue("duration", option);
                    if (formik.values.startDate) {
                      setEndDate(null, option?.value);
                    }
                  }}
                  disabled={formik.isSubmitting}
                />
              </div>
            </div>

            <ExamCadence
              title="Suggested Exam Cadence"
              options={examCadenceOptions}
              selectedOption={formik.values.examCadence}
              handleChange={(option) =>
                formik.setFieldValue("examCadence", option)
              }
              disabled={formik.isSubmitting}
              onAddButtonClick={(event) => {
                setInputPattern(TextFieldMaskENUM.examCadence);
                setIconElement(event.target as Element);
                setEditableField(RecoveryPlanItem.examCadence);
              }}
              onResetButtonClick={resetExamCadenceOptions}
            />

            <div className="flex space-x-5">
              <div className="space-y-3">
                <h5 className="text-[12px] text-[#231F20]">Start Date</h5>
                <DatePicker
                  onChange={(date: Date) => {
                    date.setHours(12, 0, 0, 0);
                    formik.setFieldValue("startDate", date);
                    setEndDate(date, null);
                    setHasUnsavedEndDate(true);
                  }}
                  date={formik.values.startDate}
                  disabled={formik.isSubmitting}
                />
              </div>

              <div className="space-y-3">
                <h5 className="text-[12px] text-[#231F20]">End Date</h5>
                <DatePicker
                  onChange={() => {}}
                  date={formik.values.endDate}
                  disabled={true}
                />
                {hasUnsavedEndDate && (
                  <FormHelperText error={Boolean(formik.errors.endDate)}>
                    Apply changes to save this value
                  </FormHelperText>
                )}
                {formik.errors.endDate && (
                  <FormHelperText error={Boolean(formik.errors.endDate)}>
                    {formik.errors.endDate}
                  </FormHelperText>
                )}
              </div>
            </div>

            <div>
              <h5 className="text-[12px] text-[#231F20]">Type</h5>
              <div className="flex items-center space-x-1 mt-2.5">
                <ButtonGroup
                  options={getTypeOptions()}
                  selectedOption={formik.values.type}
                  handleChange={(option) =>
                    formik.setFieldValue("type", option)
                  }
                  disabled={formik.isSubmitting}
                />
              </div>
            </div>
            <div>
              <h5 className="text-[12px] text-[#231F20]">Contralateral</h5>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.isContralateralNeed}
                    onChange={(ev) => {
                      formik.handleChange(ev);
                      if (ev.target.checked) {
                        formik.setFieldValue("contralateralExamCadence", null);
                      }
                    }}
                    name="isContralateralNeed"
                    value={formik.values.isContralateralNeed}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label=""
              />
            </div>
            {formik.values.isContralateralNeed && (
              <ExamCadence
                title="Contralateral Exam Cadence"
                options={contralateralExamCadenceOptions}
                selectedOption={formik.values.contralateralExamCadence}
                handleChange={(option) =>
                  formik.setFieldValue("contralateralExamCadence", option)
                }
                disabled={formik.isSubmitting}
                onAddButtonClick={(event) => {
                  setInputPattern(TextFieldMaskENUM.examCadence);
                  setIconElement(event.target as Element);
                  setEditableField(RecoveryPlanItem.contralateralExamCadence);
                }}
                onResetButtonClick={resetContralateralExamCadenceOptions}
              />
            )}
          </div>
          <div className="flex flex-col flex-1 border-l pl-10 space-y-[25px]">
            <div className="bg-white px-2 pt-1 pb-2">
              <h5 className="text-[16px] text-[#231F20] font-semibold">
                Goals
              </h5>
              <p className="text-grayDark leading-[16px] text-[12px] mt-1">
                Add goals to each exam milestone. For simplicity, goal criteria
                is auto-saved and duplicated to the next goal.
              </p>
              <div className="flex items-center space-x-1 mt-2.5">
                <ButtonGroup
                  options={goalsOptions}
                  selectedOption={selectedGoalPeriod}
                  handleChange={(option) => setSelectedGoalPeriod(option)}
                  disabled={formik.isSubmitting}
                  configuredOptions={configuredGoals}
                />
              </div>
            </div>
            <div className="bg-white px-2 pb-2">
              <h5 className="text-[16px] text-[#231F20] font-semibold">ROM</h5>
              <div className="bg-white rounded-[10px] pt-2">
                <h5 className="text-[14px] mb-1 font-bold text-[#231F20]">
                  Extension
                </h5>
                <div className="flex justify-start gap-6">
                  <div>
                    <h5 className="text-[12px] text-[#231F20]">Active</h5>
                    <div className="flex items-start space-x-1 mt-2">
                      <ButtonGroup
                        options={defaultActiveExtensionOptions}
                        selectedOption={
                          selectedGoalPeriod?.value
                            ? formik.values.goals?.[selectedGoalPeriod.value]
                                ?.activeExtension
                            : null
                        }
                        handleChange={(option) => {
                          formik.setFieldValue(
                            `goals[${selectedGoalPeriod?.value}].activeExtension`,
                            option
                          );
                        }}
                        onButtonClick={(event) => {
                          setInputPattern(TextFieldMaskENUM.extension);
                          setIconElement(event.target as Element);
                          setEditableField(RecoveryPlanItem.activeExtension);
                        }}
                        disabled={formik.isSubmitting}
                      />
                    </div>
                  </div>
                  <div>
                    <h5 className="text-[12px] text-[#231F20]">Passive</h5>
                    <div className="flex items-center space-x-1 mt-2">
                      <ButtonGroup
                        options={defaultPassiveExtensionOptions}
                        selectedOption={
                          selectedGoalPeriod?.value
                            ? formik.values.goals?.[selectedGoalPeriod.value]
                                ?.passiveExtension
                            : null
                        }
                        handleChange={(option) => {
                          formik.setFieldValue(
                            `goals[${selectedGoalPeriod?.value}].passiveExtension`,
                            option
                          );
                        }}
                        onButtonClick={(event) => {
                          setInputPattern(TextFieldMaskENUM.extension);
                          setIconElement(event.target as Element);
                          setEditableField(RecoveryPlanItem.passiveExtension);
                        }}
                        disabled={formik.isSubmitting}
                      />
                    </div>
                  </div>
                </div>
                <h5 className="text-[14px] mt-2 mb-1 font-bold text-[#231F20]">
                  Flexion
                </h5>
                <div className="flex justify-start gap-6">
                  <div>
                    <h5 className="text-[12px] text-[#231F20]">Active</h5>
                    <div className="flex items-start space-x-1 mt-2">
                      <ButtonGroup
                        options={defaultActiveFlexionOptions}
                        selectedOption={
                          selectedGoalPeriod?.value
                            ? formik.values.goals?.[selectedGoalPeriod.value]
                                ?.activeFlexion
                            : null
                        }
                        handleChange={(option) => {
                          formik.setFieldValue(
                            `goals[${selectedGoalPeriod?.value}].activeFlexion`,
                            option
                          );
                        }}
                        onButtonClick={(event) => {
                          setInputPattern(TextFieldMaskENUM.extension);
                          setIconElement(event.target as Element);
                          setEditableField(RecoveryPlanItem.activeFlexion);
                        }}
                        disabled={formik.isSubmitting}
                      />
                    </div>
                  </div>
                  <div>
                    <h5 className="text-[12px] text-[#231F20]">Passive</h5>
                    <div className="flex items-center space-x-1 mt-2">
                      <ButtonGroup
                        options={defaultPassiveFlexionOptions}
                        selectedOption={
                          selectedGoalPeriod?.value
                            ? formik.values.goals?.[selectedGoalPeriod.value]
                                ?.passiveFlexion
                            : null
                        }
                        handleChange={(option) => {
                          formik.setFieldValue(
                            `goals[${selectedGoalPeriod?.value}].passiveFlexion`,
                            option
                          );
                        }}
                        onButtonClick={(event) => {
                          setInputPattern(TextFieldMaskENUM.extension);
                          setIconElement(event.target as Element);
                          setEditableField(RecoveryPlanItem.passiveFlexion);
                        }}
                        disabled={formik.isSubmitting}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-2">
                  <div>
                    <h5 className="text-[12px] text-[#231F20]">
                      Weight Bearing
                    </h5>
                    <div className="flex items-center space-x-1 mt-2.5">
                      <ButtonGroup
                        options={WEIGHT_BEARING_OPTIONS}
                        selectedOption={
                          selectedGoalPeriod?.value
                            ? formik.values.goals?.[selectedGoalPeriod.value]
                                ?.weightBearing
                            : null
                        }
                        handleChange={(option) => {
                          formik.setFieldValue(
                            `goals[${selectedGoalPeriod?.value}].weightBearing`,
                            option
                          );
                        }}
                        disabled={formik.isSubmitting}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white px-2 pt-1 pb-2">
              <h5 className="text-[16px] text-[#231F20] font-semibold mb-2">
                Comments
              </h5>
              <TextField
                inputProps={{
                  style: {
                    resize: "vertical",
                    minHeight: "200px",
                    maxHeight: "800px",
                    height: "200px",
                  },
                }}
                size="small"
                multiline
                rows={3}
                variant="outlined"
                placeholder="Type comments here..."
                name="comments"
                fullWidth
                value={
                  selectedGoalPeriod?.value
                    ? comment[selectedGoalPeriod.value as keyof typeof comment]
                    : ""
                }
                onChange={(option) => {
                  if (selectedGoalPeriod && selectedGoalPeriod.value) {
                    setComment({
                      ...comment,
                      [selectedGoalPeriod.value]: option.target.value,
                    });
                  }
                  formik.setFieldValue(
                    `goals[${selectedGoalPeriod?.value}].comment`,
                    option.target.value
                  );
                }}
                disabled={formik.isSubmitting}
              />
            </div>
          </div>
        </div>
        <div className="w-full text-right mt-3">
          {isTemplate && (
            <Button
              style={{ borderRadius: "8px", color: "white" }}
              className="text-white btn-gradient"
              onClick={formik.submitForm}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={20} /> : "Save Template"}
            </Button>
          )}
        </div>
        <AddNewValue
          anchorEl={iconElement}
          inputPattern={inputPattern}
          onClose={closePopover}
          onSave={updateOptions}
        />
        <SuccessFailedModal
          isError={isError}
          successMessage={`Recovery plan ${
            isTemplate ? "template" : ""
          } successfully updated`}
          failMessage={`Failed to update recovery plan ${
            isTemplate ? "template" : ""
          }`}
          open={isSuccessModalOpen}
          onClose={() => {
            setIsSuccessModalOpen(false);
            setIsError(false);
          }}
        />
        <ReusableModal
          successMessage="Recovery plan deleted successfully"
          modalLabel="Confirm Deletion"
          title="Deleting Recovery Plan"
          text="Are you sure you want to proceed with the deletion of the selected recovery plan?  "
          subText="This action cannot be undone."
          isEnabled={openDeleteModal}
          setIsEnabled={setOpenDeleteModal}
          onConfirm={handleDeleteRecoveryPlan}
        />
      </div>
    </StyledPatientRecovery>
  );
};
